import clsx from 'clsx'
import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import React, {
  FC,
  FocusEvent,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { ValidatedFormContext } from 'core/components/validatedForm/ValidatedForm'
import { requiredValidator } from 'core/utils/fieldValidators'
import { pathStr, pathStrOr } from 'utils/fp'
import { memoizedObj } from 'utils/misc'
import generateTestId from 'utils/test-helpers'

// @deprecated
export const ValidatedFormInputPropTypes = {
  required: PropTypes.bool,
  validateFormOnChange: PropTypes.bool,
  validations: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  initialValue: PropTypes.any,
}
interface ChildrenFnParams<T> {
  id: string
  values?: Record<string, unknown>
  value?: T
  className?: string
  onBlur?: (event: FocusEvent<Element>) => void
  onChange?: (value: T) => void
  // @todo fix this
  getCurrentValue?: <V, C>(getterFn?: (formContext: C) => V) => V
  updateFieldValue?: (value: T) => void
  setFieldValue?: <T>(key: string) => (value: T) => void
  hasError?: boolean
  errorMessage?: string
  required?: boolean
  // optional params in DomainRolesTableField (single-consumer concern leaked into this
  // shared type; the real shape is Domain[] from account/.../domains/model.ts, but typing
  // it precisely here would require importing an account-plugin type into this shared
  // core module, so it's left as `any` rather than guessed/widened incorrectly)
  domains?: any
  preSelectedDomainIds?: string[]
}

export interface ValidatedFormInputProps<T, P> {
  id: string
  className?: string
  value?: T
  initialValue?: T
  required?: boolean
  error?: string
  validateFormOnChange?: boolean
  // Array form is FieldValidator[] in every verified call site, but the object form
  // (Record<string, ...>) shape isn't verifiable across all ~70+ consumers without
  // guessing, so this is left broad rather than narrowed incorrectly.
  validations?: any[] | Record<string, any>
  onBlur?: (event: FocusEvent<Element>) => void
  onChange?: (value: T) => void
  children: (params: ChildrenFnParams<T> & P) => JSX.Element
}

type ValidatedFormContextType = {
  initialValues: Record<string, unknown>
  values: Record<string, unknown>
  errors: Record<string, unknown>
  setFieldValue: (key: string) => (value: unknown, validate?: boolean) => void
  updateFieldValue: (value: unknown) => void
  getFieldValue: (key: string) => unknown
  defineField: (key: string) => (spec: unknown) => void
  removeField: (key: string) => void
  validateField: (key: string) => () => void
  clearFieldErrors: (key: string) => void
  showErrorsOnBlur: boolean
}

/**
 * Wrapper for all the inputs that will require some sort of interaction with
 * the ValidatedForm such as validations and text hints on hover
 */
export function ValidatedFormInput<T, P>({
  id,
  className,
  initialValue,
  validateFormOnChange = false,
  value,
  required = false,
  validations = [],
  onBlur,
  onChange,
  children,
  error,
  ...rest
}: ValidatedFormInputProps<T, P> & P) {
  const {
    initialValues,
    values,
    errors,
    setFieldValue,
    updateFieldValue,
    getFieldValue,
    defineField,
    removeField,
    validateField,
    clearFieldErrors,
    showErrorsOnBlur,
  } = useContext(ValidatedFormContext) as ValidatedFormContextType
  const defineCurrentField = defineField(id)
  const setCurrentFieldValue = setFieldValue(id)
  const updateCurrentFieldValue = updateFieldValue(id)
  const getCurrentFieldValue = getFieldValue(id)
  const validateCurrentField = validateField(id)
  const prevIdRef = useRef<string>()
  const currentInitialValue =
    initialValue !== undefined
      ? initialValue
      : id !== undefined
        ? pathStr(id, initialValues)
        : undefined
  const currentValue = isNil(value) ? (id !== undefined ? pathStr(id, values) : undefined) : value
  const hasError = pathStrOr(null, `${id}.hasError`, errors)
  const errorMessage = pathStrOr(error, `${id}.errorMessage`, errors)

  useEffect(() => {
    // If id changed from a previous value, clear any stale errors at the new id
    if (prevIdRef.current !== undefined && prevIdRef.current !== id) {
      clearFieldErrors(id)
    }
    prevIdRef.current = id

    defineCurrentField({
      validations: required
        ? Array.isArray(validations)
          ? [requiredValidator, ...validations]
          : { required: true, ...validations }
        : validations,
    })
    if (currentInitialValue !== undefined) {
      setCurrentFieldValue(currentInitialValue)
    }
    // Cleanup: remove field when id changes or component unmounts
    return () => removeField(id)
  }, [id, required, memoizedObj(validations)])

  // Notify value changes to the form when the field is controlled
  // Also sync when id changes to handle array reordering
  useEffect(() => {
    if (!isNil(value)) {
      setCurrentFieldValue(value)
    }
  }, [value, id])

  const handleBlur = useCallback(
    (e) => {
      if (showErrorsOnBlur) {
        validateCurrentField()
      }
      // Leverage the event to the wrapped input
      if (onBlur) {
        onBlur(e)
      }
    },
    [showErrorsOnBlur, validateCurrentField, onBlur],
  )

  const handleChange = useCallback(
    (value) => {
      setCurrentFieldValue(value, validateFormOnChange)
      // Leverage the event to the wrapped input
      if (onChange) {
        onChange(value)
      }
    },
    [setCurrentFieldValue, onChange],
  )

  // @ts-ignore
  const params: PropsWithFormContext<T, P> = {
    ...rest,
    id,
    values,
    className: clsx(className, 'validatedFormInput'),
    onChange: handleChange,
    onBlur: handleBlur,
    value: currentValue,
    getCurrentValue: getCurrentFieldValue,
    updateFieldValue: updateCurrentFieldValue,
    setFieldValue,
    hasError,
    errorMessage,
    required,
  }
  return children(params)
}

export type PropsWithFormContext<T, P> = ChildrenFnParams<T> & P

/**
 * withFormContext provides access to the form context through props.
 *
 * This pattern is needed because React does not provide access to context within
 * lifecycle methods (componentDidMount).
 *
 * See: https://github.com/facebook/react/issues/12397#issuecomment-375501574
 *
 * @param {Inject the form context into this Component through props.} Input
 */
export default function withFormContext<T, P>(Input: FC<P & ChildrenFnParams<T>>) {
  return forwardRef<HTMLElement, Omit<ValidatedFormInputProps<T, P>, 'children'> & P>(
    (props, ref) => (
      // @ts-ignore
      <ValidatedFormInput<T, P> {...props}>
        {(inputProps) => (
          <Input
            {...inputProps}
            ref={ref}
            data-testid={generateTestId(inputProps.id, 'list', 'table', 'field')}
          />
        )}
      </ValidatedFormInput>
    ),
  )
}
